import dayjs from 'dayjs'
import { get, onValue, push, set, update } from 'firebase/database'
import cloneDeep from 'lodash/cloneDeep'

import { AI_1 } from 'features/ai/ai-agent'
import { PlayerColor } from 'features/board/board'
import { generateWallPositions, oppositePlayer } from 'features/board/utils'
import { getRandomColor } from 'features/game/utils'
import { DEFAULT_RATING, getGameScoreForWhite, updateRatings } from 'features/user/elo-rating'

import { getKeys } from 'shared/utils'

import { gamesRef, playersRef } from './database'
import { DatabaseGame, Game, GameResult, Player, initialGameState } from './types'

const dispatchInitializeEvent = async (gameId: string, timeToPlay: number) =>
  await push(gamesRef(`all/${gameId}/events`), {
    clock: { white: timeToPlay, black: timeToPlay },
    type: 'initialize',
    metadata: { walls: generateWallPositions() },
  })

export const getGame = async (gameId: string) => {
  const game = (await get(gamesRef(`all/${gameId}`))).val() as DatabaseGame
  return game
}

export const createGame = async (
  gameId: string,
  playerId: string,
  playerRating = DEFAULT_RATING,
  gameTime = 900000,
  withAI = false,
  autoAddAI = true
) => {
  const newGameState = cloneDeep(initialGameState)
  const myColor = getRandomColor()
  newGameState.players[myColor] = playerId
  newGameState.gameId = gameId
  newGameState.ratings.before[myColor] = playerRating
  newGameState.timeToPlay = gameTime
  newGameState.createdAt = dayjs().toISOString()

  if (withAI) {
    newGameState.ai = oppositePlayer(myColor)
  }
  if (autoAddAI) {
    newGameState.autoAddAI = true
  }

  await set(gamesRef(`all/${gameId}`), newGameState)
  await set(gamesRef(`pending/${gameId}`), true)
  await set(playersRef(`${playerId}/games/open/${gameId}`), true)
  await set(playersRef(`${playerId}/games/all/${gameId}`), true)

  if (withAI) {
    await update(playersRef(AI_1), {
      displayName: AI_1,
      nickname: AI_1,
      online: true,
    } as Player)
    await joinGame(gameId, AI_1)
  }
}

export const findPendingGames = async () => {
  const pendingGameIds = ((await get(gamesRef('pending/'))).val() || []) as Record<string, boolean>

  return await Promise.all(getKeys(pendingGameIds).map(async gameId => getGame(gameId)))
}

export const joinGame = async (gameId: string, playerId: string, playerRating = DEFAULT_RATING) => {
  const game = await getGame(gameId)
  if (!game) return

  const myColor = !game.players.white ? 'white' : 'black'

  await set(gamesRef(`pending/${gameId}`), null)
  await set(gamesRef(`all/${gameId}/ratings/before/${myColor}`), playerRating)
  await set(gamesRef(`all/${gameId}/players/${myColor}`), playerId)
  await set(playersRef(`${playerId}/games/open/${gameId}`), true)
  await set(playersRef(`${playerId}/games/all/${gameId}`), true)
  await dispatchInitializeEvent(gameId, game.timeToPlay)
  await set(gamesRef(`all/${gameId}/status`), 'playing')
}

export const addAIToGame = async (gameId: string, myColor: PlayerColor) => {
  await set(gamesRef(`all/${gameId}/ai`), oppositePlayer(myColor))
  await update(playersRef(AI_1), {
    displayName: AI_1,
    nickname: AI_1,
    online: true,
  } as Player)
  await joinGame(gameId, AI_1)
}

export const abortLooking = async (gameId: string) => {
  const game = await getGame(gameId)
  if (!game) return

  if (game.players.white) {
    await set(playersRef(`${game.players.white}/games/open/${gameId}`), null)
    await set(playersRef(`${game.players.white}/games/all/${gameId}`), null)
  }

  if (game.players.black) {
    await set(playersRef(`${game.players.black}/games/open/${gameId}`), null)
    await set(playersRef(`${game.players.black}/games/all/${gameId}`), null)
  }

  await set(gamesRef(`pending/${gameId}`), null)
  await set(gamesRef(`all/${gameId}`), null)
}

const finishGame = async (gameId: string, result: GameResult) => {
  const game = await getGame(gameId)
  if (!game) return

  const resultWhite = getGameScoreForWhite(result)
  const k = game.ai ? 16 : 32
  const [ratingWhite, ratingBlack] = updateRatings(game.ratings.before.white, game.ratings.before.black, resultWhite, k)

  await set(gamesRef(`pending/${gameId}`), null)
  await update(gamesRef(`all/${gameId}`), {
    status: 'finished',
    result,
    ratings: {
      before: {
        white: game.ratings.before.white,
        black: game.ratings.before.black,
      },
      after: {
        white: ratingWhite,
        black: ratingBlack,
      },
    },
  })

  await set(playersRef(`${game.players.white}/rating`), ratingWhite)
  await set(playersRef(`${game.players.black}/rating`), ratingBlack)

  await set(playersRef(`${game.players.white}/games/open/${gameId}`), null)
  await set(playersRef(`${game.players.black}/games/open/${gameId}`), null)
}

export const finishGameByResign = async (gameId: string, color: PlayerColor) =>
  await finishGame(gameId, {
    winner: oppositePlayer(color),
    reason: 'resign',
  })

export const finishGameByDraw = async (gameId: string) =>
  await finishGame(gameId, {
    winner: 'draw',
    reason: 'draw',
  })

export const finishGameByTimeout = async (gameId: string, winner: PlayerColor) =>
  await finishGame(gameId, {
    winner,
    reason: 'timeout',
  })

export const finishGameByAbort = async (gameId: string, winner: PlayerColor) =>
  await finishGame(gameId, {
    winner,
    reason: 'abort',
  })

export const subscribeToPendingGames = (cb: (data: Record<string, boolean>) => void) =>
  onValue(gamesRef('pending'), snapshot => {
    const data = snapshot.val()
    cb(data)
  })

export const subscribeToMyGames = (playerId: string, cb: (data: Record<string, boolean>) => void) =>
  onValue(playersRef(`${playerId}/games/open`), snapshot => {
    const data = snapshot.val()
    cb(data)
  })

export const subscribeToGame = (gameId: string, cb: (gameData: Game) => void) =>
  onValue(gamesRef(`all/${gameId}`), snapshot => {
    const data = snapshot.val()
    cb(data)
  })
